import { useEffect, useState } from "react";
import {
  fetchComplianceStatus,
  fetchFindingsTrend,
  fetchNonCompliantByApplication,
  fetchNonCompliantByCrCategory,
  fetchNonCompliantByTeam,
  fetchNonCompliantDistribution,
  fetchNonCompliantWorkflowStatus,
  type LogReviewChartFilters,
} from "../api/logReviewApi";
import type {
  AreaChartData,
  NonCompliantBreakdown,
  PieChartDatum,
  RankedBarDatum,
  TreeMapNode,
} from "../app/types/logReview";

export interface LogReviewChartsData {
  complianceStatus: PieChartDatum[];
  distribution: NonCompliantBreakdown | null;
  findingsTrend: AreaChartData;
  workflowStatus: PieChartDatum[];
  byApplications: RankedBarDatum[];
  byTeam: TreeMapNode[];
  byCrCategories: RankedBarDatum[];
}

export type LogReviewChartKey = keyof LogReviewChartsData;

export interface UseLogReviewChartsResult extends LogReviewChartsData {
  /** Per-chart loading flag — each chart resolves independently. */
  loading: Record<LogReviewChartKey, boolean>;
  /** Per-chart error message — set only for the chart whose fetch failed. */
  error: Record<LogReviewChartKey, string | null>;
}

const EMPTY: LogReviewChartsData = {
  complianceStatus: [],
  distribution: null,
  findingsTrend: { categories: [], series: [] },
  workflowStatus: [],
  byApplications: [],
  byTeam: [],
  byCrCategories: [],
};

const CHART_KEYS: LogReviewChartKey[] = [
  "complianceStatus",
  "distribution",
  "findingsTrend",
  "workflowStatus",
  "byApplications",
  "byTeam",
  "byCrCategories",
];

const allLoading = (v: boolean): Record<LogReviewChartKey, boolean> =>
  Object.fromEntries(CHART_KEYS.map((k) => [k, v])) as Record<LogReviewChartKey, boolean>;
const allError = (): Record<LogReviewChartKey, string | null> =>
  Object.fromEntries(CHART_KEYS.map((k) => [k, null])) as Record<
    LogReviewChartKey,
    string | null
  >;

/**
 * Fetches every Log Review chart for the applied filter set. Each chart fires
 * its OWN request and updates its OWN data / loading / error slice — so a fast
 * endpoint renders immediately instead of waiting on the slowest one in the
 * batch. Aborts every in-flight request on unmount / re-run.
 *
 * `enabled` gates the first fetch: the dashboard mounts before the top filter
 * cascade has loaded, so fetching immediately would fire a request with empty
 * default filters that the cascade's initial apply then aborts and refires.
 * Passing `enabled={false}` until the applied filters are ready collapses that
 * "called → cancelled → called" pattern into a single call.
 */
export const useLogReviewCharts = (
  filters: LogReviewChartFilters,
  reloadKey = 0,
  enabled = true,
): UseLogReviewChartsResult => {
  const [data, setData] = useState<LogReviewChartsData>(EMPTY);
  const [loading, setLoading] = useState<Record<LogReviewChartKey, boolean>>(() =>
    allLoading(true),
  );
  const [error, setError] = useState<Record<LogReviewChartKey, string | null>>(() =>
    allError(),
  );

  // Stable primitive key so the effect re-runs only on a real filter change.
  const filtersKey = `${filters.appCode.join(",")}|${filters.category.join(",")}|${filters.startDate}|${filters.endDate}`;

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(allLoading(true));
    setError(allError());

    const run = <K extends LogReviewChartKey>(
      key: K,
      promise: Promise<LogReviewChartsData[K]>,
    ): void => {
      promise
        .then((value) => {
          if (signal.aborted) return;
          setData((d) => ({ ...d, [key]: value }));
          setLoading((l) => ({ ...l, [key]: false }));
        })
        .catch((err: unknown) => {
          if (signal.aborted) return;
          setError((e) => ({
            ...e,
            [key]: err instanceof Error ? err.message : "Failed to load chart",
          }));
          setLoading((l) => ({ ...l, [key]: false }));
        });
    };

    run("complianceStatus", fetchComplianceStatus(filters, signal));
    run("distribution", fetchNonCompliantDistribution(filters, signal));
    run("findingsTrend", fetchFindingsTrend(filters, signal));
    run("workflowStatus", fetchNonCompliantWorkflowStatus(filters, signal));
    run("byApplications", fetchNonCompliantByApplication(filters, signal));
    run("byTeam", fetchNonCompliantByTeam(filters, signal));
    run("byCrCategories", fetchNonCompliantByCrCategory(filters, signal));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, reloadKey, enabled]);

  return { ...data, loading, error };
};
